import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import InventoryIcon from '@mui/icons-material/Inventory';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axios from 'axios';
import { API_BASE_URL } from '../types';

// ==================== Types ====================

type OrderStatus =
  | 'PENDING'
  | 'FETCHING_INVENTORY'
  | 'VERIFYING_PRICES'
  | 'FETCHING_CHILD_SKUS'
  | 'GENERATING_PICKLIST'
  | 'CONFIRMING_AVAILABILITY'
  | 'DELISTING'
  | 'GENERATING_OUTPUT'
  | 'COMPLETED'
  | 'FAILED';

interface WholesaleOrder {
  id: number;
  created_at: string;
  po_filename: string;
  status: OrderStatus;
  status_detail: string | null;
  total_po_items: number | null;
  fulfill_count: number | null;
  do_not_fulfill_count: number | null;
  not_in_inventory_count: number | null;
  no_child_sku_count: number | null;
  sold_count: number | null;
  confirmed_count: number | null;
  total_value: number | null;
  primary_warehouse: string | null;
  delist_enabled: boolean;
  delist_locations: string | null;
  price_floor: number | null;
  output_filename: string | null;
  error_message: string | null;
}

type ReportStatus = 'PENDING' | 'FETCHING' | 'PARSING' | 'FILTERING' | 'MERGING' | 'UPLOADING' | 'COMPLETED' | 'FAILED';

interface InventoryReport {
  id: number;
  created_at: string;
  min_price: number;
  report_type: string;
  status: ReportStatus;
  status_detail: string | null;
  error_message: string | null;
  total_isbns: number | null;
  total_qty: number | null;
  output_filename: string | null;
  source_filename: string | null;
}

// ==================== Constants ====================

const ORDER_TERMINAL: OrderStatus[] = ['COMPLETED', 'FAILED'];
const REPORT_TERMINAL: ReportStatus[] = ['COMPLETED', 'FAILED'];

const ORDER_STATUS_COLOR: Record<OrderStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  PENDING: 'default',
  FETCHING_INVENTORY: 'info',
  VERIFYING_PRICES: 'info',
  FETCHING_CHILD_SKUS: 'info',
  GENERATING_PICKLIST: 'info',
  CONFIRMING_AVAILABILITY: 'info',
  DELISTING: 'warning',
  GENERATING_OUTPUT: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};

const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: 'Queued',
  FETCHING_INVENTORY: 'Fetching Inventory...',
  VERIFYING_PRICES: 'Verifying Prices...',
  FETCHING_CHILD_SKUS: 'Fetching Child SKUs...',
  GENERATING_PICKLIST: 'Generating Picklist...',
  CONFIRMING_AVAILABILITY: 'Confirming Availability...',
  DELISTING: 'Delisting...',
  GENERATING_OUTPUT: 'Generating Output...',
  COMPLETED: 'Complete',
  FAILED: 'Failed',
};

const REPORT_STATUS_COLOR: Record<ReportStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  PENDING: 'default',
  FETCHING: 'info',
  PARSING: 'info',
  FILTERING: 'info',
  MERGING: 'info',
  UPLOADING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};

const REPORT_STATUS_LABEL: Record<ReportStatus, string> = {
  PENDING: 'Queued',
  FETCHING: 'Fetching...',
  PARSING: 'Parsing...',
  FILTERING: 'Filtering...',
  MERGING: 'Merging...',
  UPLOADING: 'Uploading...',
  COMPLETED: 'Complete',
  FAILED: 'Failed',
};

// ==================== Helpers ====================

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(val: number | null): string {
  if (val == null) return '-';
  return `$${val.toFixed(2)}`;
}

function fmtNumber(val: number | null): string {
  if (val == null) return '-';
  return val.toLocaleString();
}

// ==================== Purchase Orders Tab ====================

function PurchaseOrdersTab() {
  const { getToken } = useAuth();

  const [orders, setOrders] = useState<WholesaleOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [delistZB, setDelistZB] = useState(false);
  const [delistZE, setDelistZE] = useState(false);
  const [priceFloor, setPriceFloor] = useState('5.00');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOrders = useCallback(async () => {
    try {
      const token = await getToken({ template: 'covervision-backend' });
      const res = await axios.get<WholesaleOrder[]>(`${API_BASE_URL}/wholesale/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data);
      setOrdersError(null);
    } catch (err: any) {
      setOrdersError(err?.response?.data?.detail || 'Failed to load orders.');
    } finally {
      setOrdersLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    const hasActive = orders.some((o) => !ORDER_TERMINAL.includes(o.status));
    if (hasActive) {
      if (!pollRef.current) pollRef.current = setInterval(fetchOrders, 5000);
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [orders, fetchOrders]);

  const handleFileSelect = (f: File) => {
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      setUploadError('Please select an Excel file (.xlsx or .xls).');
      setFile(null);
      return;
    }
    setUploadError(null);
    setFile(f);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFileSelect(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    const formData = new FormData();
    formData.append('file', file);
    const parts: string[] = [];
    if (delistZB) parts.push('168');
    if (delistZE) parts.push('197');
    const delistLocations = parts.join(',');
    const params = new URLSearchParams();
    params.set('delist_locations', delistLocations);
    const pf = parseFloat(priceFloor);
    if (!isNaN(pf) && pf >= 0) params.set('price_floor', pf.toString());
    try {
      const token = await getToken({ template: 'covervision-backend' });
      await axios.post(`${API_BASE_URL}/wholesale/upload?${params.toString()}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setFile(null);
      await fetchOrders();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setUploadError(typeof detail === 'string' ? detail : 'Upload failed. Check server logs.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (order: WholesaleOrder) => {
    try {
      const token = await getToken({ template: 'covervision-backend' });
      const res = await axios.get(`${API_BASE_URL}/wholesale/orders/${order.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.open(res.data.download_url, '_blank');
    } catch (err: any) {
      console.error('Download failed:', err);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Upload Purchase Order</Typography>
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'grey.400',
            borderRadius: 2, p: 4, textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            bgcolor: dragOver ? 'action.hover' : 'background.paper',
            transition: 'all 0.15s ease',
            '&:hover': uploading ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500' }} />
            {file ? (
              <Typography variant="body1">{file.name}</Typography>
            ) : (
              <>
                <Typography variant="body1">Drag & drop a PO Excel file here, or click to browse</Typography>
                <Typography variant="caption" color="text.secondary">Accepts .xlsx or .xls files</Typography>
              </>
            )}
          </Box>
        </Box>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onFileChange} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Checkbox checked={delistZB} onChange={(e) => setDelistZB(e.target.checked)} size="small" />}
            label="Delist ZB (BC)"
          />
          <FormControlLabel
            control={<Checkbox checked={delistZE} onChange={(e) => setDelistZE(e.target.checked)} size="small" />}
            label="Delist ZE (Ontario)"
          />
          <TextField
            label="Price Floor"
            type="number"
            size="small"
            value={priceFloor}
            onChange={(e) => setPriceFloor(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.5 }}
            helperText="For POs without prices"
            sx={{ width: 160 }}
          />
          <Button
            variant="contained" onClick={handleUpload} disabled={!file || uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
        {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}
      </Paper>

      <Typography variant="h6" gutterBottom>Order History</Typography>
      {ordersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : ordersError ? (
        <Alert severity="error">{ordersError}</Alert>
      ) : orders.length === 0 ? (
        <Typography color="text.secondary">No orders yet.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>PO File</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Detail</strong></TableCell>
                <TableCell align="right"><strong>Items</strong></TableCell>
                <TableCell align="right"><strong>Confirmed</strong></TableCell>
                <TableCell align="right"><strong>Sold</strong></TableCell>
                <TableCell align="right"><strong>Value ($)</strong></TableCell>
                <TableCell><strong>Delist</strong></TableCell>
                <TableCell><strong>Download</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>{fmtDate(order.created_at)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.po_filename}
                  </TableCell>
                  <TableCell>
                    <Chip label={ORDER_STATUS_LABEL[order.status] || order.status} color={ORDER_STATUS_COLOR[order.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.status_detail || '-'}
                  </TableCell>
                  <TableCell align="right">{order.total_po_items ?? '-'}</TableCell>
                  <TableCell align="right">{order.confirmed_count ?? '-'}</TableCell>
                  <TableCell align="right">{order.sold_count ?? '-'}</TableCell>
                  <TableCell align="right">{fmtCurrency(order.total_value)}</TableCell>
                  <TableCell>{
                    order.delist_locations
                      ? order.delist_locations.split(',').map((s: string) => {
                          const id = s.trim();
                          if (id === '168') return 'ZB';
                          if (id === '197') return 'ZE';
                          return id;
                        }).join(', ')
                      : order.delist_enabled ? 'Yes' : 'No'
                  }</TableCell>
                  <TableCell>
                    {order.status === 'COMPLETED' && order.output_filename ? (
                      <IconButton size="small" onClick={() => handleDownload(order)} title="Download output">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ==================== Inventory Reports Tab ====================

function InventoryReportsTab() {
  const { getToken } = useAuth();

  const [reports, setReports] = useState<InventoryReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState<string | null>(null);

  const [minPrice, setMinPrice] = useState('4.00');
  const [amazonMinPrice, setAmazonMinPrice] = useState('4.00');
  const [generating, setGenerating] = useState(false);
  const [generatingAmazon, setGeneratingAmazon] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [amazonError, setAmazonError] = useState<string | null>(null);

  // External upload state
  const [extFile, setExtFile] = useState<File | null>(null);
  const [extUploading, setExtUploading] = useState(false);
  const [extError, setExtError] = useState<string | null>(null);
  const [extDragOver, setExtDragOver] = useState(false);
  const extFileInputRef = useRef<HTMLInputElement>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchReports = useCallback(async () => {
    try {
      const token = await getToken({ template: 'covervision-backend' });
      const res = await axios.get<InventoryReport[]>(`${API_BASE_URL}/wholesale/inventory-reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(res.data);
      setReportsError(null);
    } catch (err: any) {
      setReportsError(err?.response?.data?.detail || 'Failed to load reports.');
    } finally {
      setReportsLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  useEffect(() => {
    const hasActive = reports.some((r) => !REPORT_TERMINAL.includes(r.status));
    if (hasActive) {
      if (!pollRef.current) pollRef.current = setInterval(fetchReports, 5000);
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [reports, fetchReports]);

  const handleGenerate = async (reportType: 'full' | 'amazon_wants') => {
    const priceStr = reportType === 'amazon_wants' ? amazonMinPrice : minPrice;
    const price = parseFloat(priceStr);
    const setError = reportType === 'amazon_wants' ? setAmazonError : setGenerateError;
    const setLoading = reportType === 'amazon_wants' ? setGeneratingAmazon : setGenerating;

    if (isNaN(price) || price < 0) {
      setError('Please enter a valid minimum price.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getToken({ template: 'covervision-backend' });
      await axios.post(
        `${API_BASE_URL}/wholesale/inventory-reports`,
        { min_price: price, report_type: reportType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchReports();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const handleExtFileSelect = (f: File) => {
    if (!f.name.match(/\.(csv|tsv|txt|xlsx|xls)$/i)) {
      setExtError('Please select a CSV, TSV, or Excel file.');
      setExtFile(null);
      return;
    }
    setExtError(null);
    setExtFile(f);
  };

  const handleExtUpload = async () => {
    if (!extFile) return;
    setExtUploading(true);
    setExtError(null);
    const formData = new FormData();
    formData.append('file', extFile);
    try {
      const token = await getToken({ template: 'covervision-backend' });
      await axios.post(`${API_BASE_URL}/wholesale/inventory-reports/upload-external`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setExtFile(null);
      await fetchReports();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setExtError(typeof detail === 'string' ? detail : 'Upload failed. Check server logs.');
    } finally {
      setExtUploading(false);
    }
  };

  const handleDownload = async (report: InventoryReport) => {
    try {
      const token = await getToken({ template: 'covervision-backend' });
      const res = await axios.get(`${API_BASE_URL}/wholesale/inventory-reports/${report.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.open(res.data.download_url, '_blank');
    } catch (err: any) {
      console.error('Download failed:', err);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Generate Inventory Report</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fetches live inventory from both warehouses (ZB + ZE), merges by ISBN, and applies a minimum price floor.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Minimum Price"
            type="number"
            size="small"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.5 }}
            sx={{ width: 160 }}
          />
          <Button
            variant="contained"
            onClick={() => handleGenerate('full')}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <InventoryIcon />}
          >
            {generating ? 'Requesting...' : 'Generate Report'}
          </Button>
        </Box>
        {generateError && <Alert severity="error" sx={{ mt: 2 }}>{generateError}</Alert>}
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Amazon Wants Report</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Same as above, but filtered to only books Amazon wants to buy. Matches your inventory against Amazon's want list.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Minimum Price"
            type="number"
            size="small"
            value={amazonMinPrice}
            onChange={(e) => setAmazonMinPrice(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.5 }}
            sx={{ width: 160 }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleGenerate('amazon_wants')}
            disabled={generatingAmazon}
            startIcon={generatingAmazon ? <CircularProgress size={20} color="inherit" /> : <FilterListIcon />}
          >
            {generatingAmazon ? 'Requesting...' : 'Generate Amazon Wants Report'}
          </Button>
        </Box>
        {amazonError && <Alert severity="error" sx={{ mt: 2 }}>{amazonError}</Alert>}
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>External Inventory Check</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a third-party inventory file (CSV/TSV/Excel) to check which ISBNs Amazon wants. ISBN-10s are automatically converted to ISBN-13.
        </Typography>
        <Box
          onDragOver={(e) => { e.preventDefault(); setExtDragOver(true); }}
          onDragLeave={() => setExtDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setExtDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleExtFileSelect(f); }}
          onClick={() => !extUploading && extFileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: extDragOver ? 'info.main' : 'grey.400',
            borderRadius: 2, p: 3, textAlign: 'center',
            cursor: extUploading ? 'not-allowed' : 'pointer',
            bgcolor: extDragOver ? 'action.hover' : 'background.paper',
            transition: 'all 0.15s ease',
            '&:hover': extUploading ? {} : { borderColor: 'info.main', bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <UploadFileIcon sx={{ fontSize: 36, color: 'grey.500' }} />
            {extFile ? (
              <Typography variant="body1">{extFile.name}</Typography>
            ) : (
              <>
                <Typography variant="body1">Drag & drop an inventory file here, or click to browse</Typography>
                <Typography variant="caption" color="text.secondary">Accepts .csv, .tsv, .txt, .xlsx, or .xls</Typography>
              </>
            )}
          </Box>
        </Box>
        <input ref={extFileInputRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleExtFileSelect(f); e.target.value = ''; }} />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="info"
            onClick={handleExtUpload}
            disabled={!extFile || extUploading}
            startIcon={extUploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
          >
            {extUploading ? 'Uploading...' : 'Check Against Amazon Wants'}
          </Button>
        </Box>
        {extError && <Alert severity="error" sx={{ mt: 2 }}>{extError}</Alert>}
      </Paper>

      <Typography variant="h6" gutterBottom>Report History</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Reports are automatically deleted after 3 days.
      </Typography>
      {reportsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : reportsError ? (
        <Alert severity="error">{reportsError}</Alert>
      ) : reports.length === 0 ? (
        <Typography color="text.secondary">No reports yet.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Detail</strong></TableCell>
                <TableCell align="right"><strong>Min Price</strong></TableCell>
                <TableCell align="right"><strong>ISBNs</strong></TableCell>
                <TableCell align="right"><strong>Total Qty</strong></TableCell>
                <TableCell><strong>Download</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell>{fmtDate(report.created_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={report.report_type === 'external_amazon_wants' ? 'External' : report.report_type === 'amazon_wants' ? 'Amazon Wants' : 'Full'}
                      color={report.report_type === 'external_amazon_wants' ? 'info' : report.report_type === 'amazon_wants' ? 'secondary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={REPORT_STATUS_LABEL[report.status] || report.status}
                      color={REPORT_STATUS_COLOR[report.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {report.status_detail || '-'}
                  </TableCell>
                  <TableCell align="right">{fmtCurrency(report.min_price)}</TableCell>
                  <TableCell align="right">{fmtNumber(report.total_isbns)}</TableCell>
                  <TableCell align="right">{fmtNumber(report.total_qty)}</TableCell>
                  <TableCell>
                    {report.status === 'COMPLETED' && report.output_filename ? (
                      <IconButton size="small" onClick={() => handleDownload(report)} title="Download report">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ==================== Main Page ====================

export default function WholesaleOrders() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Wholesale</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Purchase Orders" />
        <Tab label="Inventory Reports" />
      </Tabs>
      {tab === 0 && <PurchaseOrdersTab />}
      {tab === 1 && <InventoryReportsTab />}
    </Box>
  );
}
